import {
  Box,
  Button,
  Text,
  useBreakpointValue,
  VStack,
  HStack,
  SimpleGrid,
} from '@chakra-ui/react';
import { ArrowRightIcon } from '@chakra-ui/icons';
import { Field, Form } from 'react-final-form';
import SwiperClass from 'swiper/types/swiper-class';
import { useEffect, useRef } from 'react';
import useInPageSurvey, {
  InPageSurvey,
  InPageSurveyFormValues,
  SurveyQuestion,
} from '../hooks/useInPageSurvey';
import { inPageSurveyQuestionType } from '../common/libs/inPageSurveyQuestionType';

/**
 * アンケートページ（インページサーベイ用：通常版）
 *
 *   1ページにつき1問を扱う
 *   注）サーベイページ表示時のGAイベント送信処理は親コンポーネント側で（Swiperのページめくりをトリガーに）行う
 *      現状、設問の形式は単数選択式のみ対応
 */
export default function SurveyPage({
  survey,
  question,
  pageIndex,
  swiper,
  onRemoveSurveyPage,
}: {
  survey: InPageSurvey;
  question: SurveyQuestion;
  pageIndex: number;
  swiper: SwiperClass | null;
  onRemoveSurveyPage?: (
    surveyId: number,
    questionNumber: number,
    removeIndex: number,
  ) => void;
}) {
  const { answerSurvey, skipSurvey } = useInPageSurvey();

  const initialFormValues: InPageSurveyFormValues = {
    surveyId: survey.surveyId,
    questionNumber: question.questionNumber,
    answer: null,
  };
  const shouldUseTwoColumns = (question.choices?.length ?? 0) >= 6;
  const shouldFitChoicesToPage = useBreakpointValue({
    base: true,
    sm: true,
    md: true,
    lg: true,
    xl: true,
    '2xl': true,
  });

  // ＜スワイプ閾値の動的変更用＞
  // 選択肢／スキップボタンをタップした際、ポインタの移動を検出してスワイプが発動してしまう事象が発生した。
  // 複数選択型、かつ回答ボタンなしの設問では、スワイプの誤発動により回答が確定してしまうため、
  // スワイプの閾値を引き上げることで対処した。
  // アクティブなサーベイページが表示されている間のみ Swiper の threshold を引き上げる。
  // 既存ページ遷移 UX を損なわないよう、非表示時には元の値へ戻す。
  const originalThresholdRef = useRef<number | null>(null);
  const RAISED_THRESHOLD = 28; // 誤タップで 10〜15px 程度動く想定より十分高めに設定

  useEffect(() => {
    if (!swiper) return;
    const isVisible = swiper.activeIndex === pageIndex;

    if (isVisible) {
      // ページ表示時に閾値を引き上げ
      if (originalThresholdRef.current === null) {
        originalThresholdRef.current = (swiper.params as any).threshold ?? 0;
      }
      if ((swiper.params as any).threshold !== RAISED_THRESHOLD) {
        (swiper.params as any).threshold = RAISED_THRESHOLD;
        swiper.update();
      }
    } else {
      // ページ表示終了時に元の値に戻す
      if (originalThresholdRef.current !== null) {
        (swiper.params as any).threshold = originalThresholdRef.current;
        swiper.update();
        originalThresholdRef.current = null;
      }
    }
  }, [swiper?.activeIndex, pageIndex, swiper]);

  const onAnswerSurvey = async (props: {
    surveyId: number;
    questionNumber: number;
    answer: number | number[] | string;
  }) => {
    await answerSurvey(props);
    if (onRemoveSurveyPage) {
      onRemoveSurveyPage(props.surveyId, props.questionNumber, pageIndex);
    }
  };

  const onSkipSurvey = async (props: {
    surveyId: number;
    questionNumber: number;
  }) => {
    await skipSurvey(props);
    if (onRemoveSurveyPage) {
      onRemoveSurveyPage(props.surveyId, props.questionNumber, pageIndex);
    }
  };

  type OptionButtonProps = {
    caption: string;
    selected: boolean;
    onClick: () => void;
  };
  const OptionButton: React.FC<OptionButtonProps> = ({
    caption,
    selected,
    onClick,
  }) => {
    return (
      <Button
        type="button"
        height={
          shouldFitChoicesToPage
            ? '100%'
            : {
                base: '44px',
                sm: '48px',
                md: '56px',
                lg: '60px',
                xl: '72px',
                '2xl': '80px',
              }
        }
        minHeight={shouldFitChoicesToPage ? '0' : undefined}
        width="100%"
        onClick={onClick}
        padding={{
          base: '4px 8px 4px 12px',
          sm: '12px 12px 12px 18px',
          lg: '10px 12px 10px 18px',
          xl: '16px 16px 16px 24px',
          '2xl': '18px 20px 18px 28px',
        }}
        margin="0"
        borderRadius={{
          base: '9px',
          sm: '10px',
          md: '11px',
          lg: '12px',
          xl: '14px',
          '2xl': '18px',
        }}
        backgroundColor={selected ? 'brand.500' : 'white'}
        boxShadow="6px 6px 8px gray"
        wordBreak={'break-word'}
        whiteSpace={'normal'}
        justifyContent="flex-start"
        textAlign="left"
      >
        <VStack spacing={0} align="flex-start" width="100%">
          <Text
            fontSize={{
              base: '10px',
              sm: '10px',
              md: '11px',
              lg: '12px',
              xl: '13px',
              '2xl': 'xl',
            }}
            fontWeight="550"
            color={selected ? 'gray.100' : 'brand.500'}
            textAlign="left"
            lineHeight={{
              base: '1.2em',
              md: '1.2',
              lg: '1.2',
              xl: '1.25',
              '2xl': '1.3',
            }}
            whiteSpace="pre-line"
            width="100%"
            sx={{
              '@media screen and (min-width: 2560px)': {
                fontSize: '1.5rem',
                lineHeight: '1.3',
              },
              '@media screen and (min-width: 3840px)': {
                fontSize: '5rem',
                lineHeight: '1.2',
              },
            }}
          >
            {caption}
          </Text>
        </VStack>
      </Button>
    );
  };

  const questionLabel = (
    <Text
      fontSize={{
        base: '11px',
        sm: '12px',
        md: '13px',
        lg: '14px',
        xl: '15px',
        '2xl': '3xl',
      }}
      fontWeight="990"
      color="textPrimary"
      textAlign="left"
      lineHeight={{
        base: '1.3',
        sm: '1.3',
        md: '1.3',
        lg: '1.35',
        xl: '1.35',
        '2xl': '1.4',
      }}
      whiteSpace="pre-line"
      width="100%"
      sx={{
        '@media screen and (min-width: 3840px)': {
          fontSize: '3.75rem',
          lineHeight: '1.3',
        },
      }}
    >
      {`Ｑ．${question.question}`}
    </Text>
  );

  return (
    <Box
      height={'100%'}
      width={'100%'}
      bgImage={'/images/survey_background.png'}
    >
      <VStack
        height={'100%'}
        width={'100%'}
        py={{ base: 1, md: 2, lg: 2, xl: 3, '2xl': 4 }}
        justifyContent={'stretch'}
        spacing={0}
      >
        <VStack
          bgColor="rgb(255 255 255 / 60%)"
          pt={{ base: 1, md: 1.5, lg: 1, xl: 2, '2xl': 3 }}
          pb={0}
          px={{ base: 1, md: 3, '2xl': 4 }}
          spacing={0}
          width={'100%'}
          flex={shouldFitChoicesToPage ? '0 0 25%' : '0 0 auto'}
          justifyContent="center"
          minH={shouldFitChoicesToPage ? '0' : undefined}
          overflowY="auto"
        >
          {questionLabel}
        </VStack>

        <Form
          initialValues={initialFormValues}
          onSubmit={() => {}}
          render={() => {
            return (
              <Box
                alignContent="center"
                px={{ base: 2, md: 3, '2xl': 4 }}
                pt={{ base: 2, md: 2, lg: 3, '2xl': 4 }}
                pb={{ base: 1, md: 1.5, lg: 1, xl: 2, '2xl': 3 }}
                w="100%"
                flex={shouldFitChoicesToPage ? '0 0 62.5%' : '1'}
                minH="0"
                display="flex"
                alignItems="stretch"
              >
                {question.questionType ===
                  inPageSurveyQuestionType.SINGLE_CHOICE &&
                  question.choices && (
                    <Box as="form" width="100%" height="100%">
                      <SimpleGrid
                        columns={shouldUseTwoColumns ? 2 : 1}
                        spacing={
                          shouldFitChoicesToPage
                            ? { base: 2, md: 2, lg: 3 }
                            : { base: 1, md: 2 }
                        }
                        width={{ base: '100%', md: '80%' }}
                        height={shouldFitChoicesToPage ? '100%' : 'auto'}
                        gridTemplateRows={
                          shouldFitChoicesToPage
                            ? `repeat(${Math.ceil(question.choices.length / (shouldUseTwoColumns ? 2 : 1))}, minmax(0, 1fr))`
                            : undefined
                        }
                        alignItems="stretch"
                        mx="auto"
                      >
                        <Field name="answer">
                          {({}) => (
                            <>
                              {question.choices!.map((choice) => (
                                <OptionButton
                                  key={`survey-${survey.surveyId}-q${question.questionNumber}-c${choice.choiceNumber}`}
                                  caption={choice.choiceName}
                                  selected={false}
                                  onClick={() => {
                                    onAnswerSurvey({
                                      surveyId: survey.surveyId,
                                      questionNumber: question.questionNumber,
                                      answer: choice.choiceNumber,
                                    });
                                  }}
                                />
                              ))}
                            </>
                          )}
                        </Field>
                      </SimpleGrid>
                    </Box>
                  )}
                {/* TODO: INPUTおよびMULTIPLE＿CHOICEは未対応（将来的に実装予定） */}
              </Box>
            );
          }}
        />

        <HStack
          alignItems={'end'}
          w="100%"
          justifyContent={'space-between'}
          px={{ base: 1, md: 3, '2xl': 4 }}
          flex={shouldFitChoicesToPage ? '0 0 12.5%' : '0 0 auto'}
          minH={shouldFitChoicesToPage ? '0' : undefined}
          overflowY="auto"
          overflowX="hidden"
        >
          <Text
            fontSize={{
              base: 'small',
              sm: 'small',
              md: 'medium',
              lg: 'medium',
              xl: 'large',
              '2xl': '2xl',
            }}
            fontWeight="bold"
            color="textWhite"
            textAlign="left"
            textShadow="2px 2px 2px gray"
            noOfLines={1}
          >
            {`${!!survey.title ? survey.title : ''}` +
              `【${question.questionNumber} / ${survey.numberOfQuestions}】`}
          </Text>

          <Button
            colorScheme="brand"
            width={{
              base: '80px',
              sm: '100px',
              md: '100px',
              lg: '120px',
              xl: '120px',
              '2xl': '168px',
            }}
            height={{
              base: '30px',
              sm: '35px',
              md: '35px',
              lg: '36px',
              xl: '40px',
              '2xl': '56px',
            }}
            type="submit"
            boxShadow={'1px 1px 2px gray'}
            borderRadius={{
              base: '12px',
              sm: '12px',
              md: '12px',
              lg: '12px',
              xl: '14px',
              '2xl': '18px',
            }}
            backgroundColor="brand.500"
            fontSize={{
              base: 'small',
              md: 'small',
              lg: 'sm',
              xl: 'medium',
              '2xl': 'xl',
            }}
            onClick={() => {
              onSkipSurvey({
                surveyId: survey.surveyId,
                questionNumber: question.questionNumber,
              });
            }}
          >
            スキップ
            <ArrowRightIcon pl={1} />
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
